import { useEffect, useMemo, useState } from "react";
import { api, fmt$, STATUS_COLORS } from "@/lib/api";
import { ChevronLeft, ChevronRight, Users } from "lucide-react";
import { Avatar } from "@/components/site/Avatar";

// Get Monday of the week containing `d`
function startOfWeek(d) {
  const x = new Date(d);
  const day = x.getDay(); // 0=Sun..6=Sat
  const diff = (day + 6) % 7; // Monday-start offset
  x.setDate(x.getDate() - diff);
  x.setHours(0, 0, 0, 0);
  return x;
}
const iso = (d) => d.toISOString().slice(0, 10);
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function AdminCalendar() {
  const [rows, setRows] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [anchor, setAnchor] = useState(() => startOfWeek(new Date()));
  const [confirmedOnly, setConfirmedOnly] = useState(false);
  const [showAvailability, setShowAvailability] = useState(true);

  useEffect(() => {
    api.get("/admin/bookings").then((r) => setRows(r.data));
    api.get("/admin/availability").then((r) => setAvailability(r.data)).catch(() => {});
  }, []);

  const days = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(anchor);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [anchor]);

  const CONFIRMED_STATUSES = ["Confirmed", "In Progress", "Completed", "Paid", "Awaiting Deposit"];

  const bookingsByDate = useMemo(() => {
    const map = {};
    for (const b of rows) {
      if (confirmedOnly && !CONFIRMED_STATUSES.includes(b.status)) continue;
      const key = b.confirmed_date || b.preferred_date;
      if (!key) continue;
      (map[key] = map[key] || []).push(b);
    }
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) =>
        (a.confirmed_time || a.preferred_time || "").localeCompare(b.confirmed_time || b.preferred_time || "")
      );
    }
    return map;
  }, [rows, confirmedOnly]);

  // Compute list of available detailers for a given date
  const availableForDate = (dateObj) => {
    const iso_key = iso(dateObj);
    const weekday = dateObj.getDay(); // 0=Sun ... 6=Sat, matches EmployeePortal DAYS index
    const out = [];
    for (const emp of availability) {
      // Check exceptions first (whole-day-off wins)
      const ex = (emp.exceptions || []).find((x) => x.date === iso_key);
      if (ex && ex.available === false) continue;
      // Look up weekly slot
      const slot = (emp.weekly || []).find((w) => w.weekday === weekday);
      if (!slot) continue;
      out.push({
        employee_id: emp.employee_id,
        name: emp.name,
        photo_data_uri: emp.photo_data_uri,
        start: slot.start || "09:00",
        end: slot.end || "17:00",
      });
    }
    return out;
  };

  const shift = (weeks) => {
    const d = new Date(anchor);
    d.setDate(d.getDate() + weeks * 7);
    setAnchor(startOfWeek(d));
  };

  const today = iso(new Date());
  const rangeLabel = `${days[0].toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${days[6].toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <h1 className="font-display text-4xl">Calendar</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-300 px-3 py-2 rounded-lg border border-zinc-700 cursor-pointer hover:border-[#CCFF00]"
            data-testid="cal-confirmed-only-label">
            <input type="checkbox" data-testid="cal-confirmed-only-toggle"
              checked={confirmedOnly} onChange={(e) => setConfirmedOnly(e.target.checked)}
              className="accent-[#CCFF00]" />
            Confirmed only
          </label>
          <label className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-300 px-3 py-2 rounded-lg border border-zinc-700 cursor-pointer hover:border-[#CCFF00]"
            data-testid="cal-availability-label">
            <input type="checkbox" data-testid="cal-availability-toggle"
              checked={showAvailability} onChange={(e) => setShowAvailability(e.target.checked)}
              className="accent-[#CCFF00]" />
            Show availability
          </label>
          <button data-testid="cal-prev-week" onClick={() => shift(-1)}
            className="p-2 rounded-lg border border-zinc-700 hover:border-[#CCFF00]">
            <ChevronLeft size={16} />
          </button>
          <button data-testid="cal-today" onClick={() => setAnchor(startOfWeek(new Date()))}
            className="px-3 py-2 rounded-lg text-xs uppercase tracking-widest border border-zinc-700 hover:border-[#CCFF00]">
            Today
          </button>
          <button data-testid="cal-next-week" onClick={() => shift(1)}
            className="p-2 rounded-lg border border-zinc-700 hover:border-[#CCFF00]">
            <ChevronRight size={16} />
          </button>
          <div className="ml-3 text-sm text-zinc-400">{rangeLabel}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
        {days.map((d, i) => {
          const key = iso(d);
          const list = bookingsByDate[key] || [];
          const isToday = key === today;
          const avail = showAvailability ? availableForDate(d) : [];
          return (
            <div key={key} data-testid={`cal-day-${key}`}
              className={`card-surface p-3 min-h-[220px] ${isToday ? "border-[#CCFF00]/60" : ""}`}>
              <div className="flex items-baseline justify-between mb-3">
                <div className="text-xs uppercase tracking-widest text-zinc-500">{DAY_LABELS[i]}</div>
                <div className={`font-display text-2xl ${isToday ? "text-[#CCFF00]" : ""}`}>{d.getDate()}</div>
              </div>
              <div className="space-y-2">
                {list.length === 0 && <div className="text-xs text-zinc-600">—</div>}
                {list.map((b) => (
                  <div key={b.id} data-testid={`cal-event-${b.id}`}
                    className={`rounded-lg border p-2 text-xs ${STATUS_COLORS[b.status] || "border-zinc-700"}`}>
                    <div className="font-mono text-[11px] opacity-90">{b.confirmed_time || b.preferred_time}</div>
                    <div className="font-semibold text-white truncate">{b.customer_name}</div>
                    <div className="text-zinc-400 truncate">{b.totals?.service_name} · {b.vehicle_size}</div>
                    {(b.assigned_employees && b.assigned_employees.length > 0) ? (
                      <div className="mt-1 space-y-0.5">
                        {b.assigned_employees.map((e) => (
                          <div key={e.id} className="flex items-center gap-1.5">
                            <Avatar src={e.photo_data_uri} name={e.name} size={16} />
                            <div className="text-[10px] text-[#CCFF00] truncate">{e.name}</div>
                          </div>
                        ))}
                      </div>
                    ) : b.assigned_employee_name && (
                      <div className="flex items-center gap-1.5 mt-1">
                        <Avatar src={b.assigned_employee_photo} name={b.assigned_employee_name} size={16} />
                        <div className="text-[10px] text-[#CCFF00] truncate">{b.assigned_employee_name}</div>
                      </div>
                    )}
                    <div className="text-[10px] mt-1 opacity-80">{fmt$(b.totals?.total)}</div>
                  </div>
                ))}
              </div>
              {showAvailability && (
                <div className="mt-3 pt-3 border-t border-zinc-800" data-testid={`cal-avail-${key}`}>
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                    <Users size={11} /> Available
                  </div>
                  {avail.length === 0 ? (
                    <div className="text-[11px] text-zinc-600">No one scheduled</div>
                  ) : (
                    <div className="space-y-1.5">
                      {avail.map((e) => (
                        <div key={e.employee_id} data-testid={`cal-avail-emp-${key}-${e.employee_id}`}
                          className="flex items-center gap-1.5 text-[11px] text-zinc-300">
                          <Avatar src={e.photo_data_uri} name={e.name} size={16} />
                          <span className="truncate flex-1">{e.name}</span>
                          <span className="font-mono text-[10px] text-zinc-500 shrink-0">{e.start}–{e.end}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
